import React, { useState, useEffect, useCallback } from 'react';
import {
  GxStandardAction,
  FeedItemSeverity,
  CreateStandardActionData,
  UpdateStandardActionData,
  getStandardActions,
  createStandardAction,
  updateStandardAction,
  deleteStandardAction,
} from '../../api/guestExperience';

interface ActionManagerProps {
  eventId: string;
}

const styles: Record<string, React.CSSProperties> = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
  },
  icon: {
    fontSize: '1.25rem',
    flexShrink: 0,
  },
  actionInfo: {
    flex: 1,
    minWidth: 0,
  },
  actionLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#0f2d1f',
  },
  actionMeta: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.15rem',
  },
  severityBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  statusBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  activeStatus: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  inactiveStatus: {
    backgroundColor: '#fdecea',
    color: '#d32f2f',
  },
  actionBtn: {
    padding: '0.3rem 0.5rem',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  addButton: {
    padding: '0.6rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
  modal: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1.5rem',
    width: '100%',
    maxWidth: '400px',
  },
  modalTitle: {
    color: '#1a6b3c',
    marginBottom: '1rem',
    fontSize: '1.1rem',
    margin: '0 0 1rem 0',
  },
  field: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  saveBtn: {
    width: '100%',
    padding: '0.7rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  cancelBtn: {
    width: '100%',
    padding: '0.7rem',
    backgroundColor: '#eee',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  empty: {
    color: '#999',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '0.75rem',
    fontSize: '0.8rem',
  },
};

const SEVERITY_COLORS: Record<FeedItemSeverity, { bg: string; color: string }> = {
  Low: { bg: '#e3f2fd', color: '#1565c0' },
  Medium: { bg: '#fff3e0', color: '#e65100' },
  High: { bg: '#fce4ec', color: '#c62828' },
  Critical: { bg: '#f3e5f5', color: '#6a1b9a' },
};

interface ActionFormData {
  label: string;
  icon: string;
  severity: FeedItemSeverity;
}

const emptyForm: ActionFormData = { label: '', icon: '⚡', severity: 'Low' };

export function ActionManager({ eventId }: ActionManagerProps) {
  const [actions, setActions] = useState<GxStandardAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState<GxStandardAction | null>(null);
  const [form, setForm] = useState<ActionFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchActions = useCallback(async () => {
    try {
      const data = await getStandardActions(eventId);
      setActions(data);
    } catch {
      setActions([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLoading(true);
    fetchActions();
  }, [fetchActions]);

  function openAdd() {
    setForm(emptyForm);
    setEditingAction(null);
    setError('');
    setShowModal(true);
  }

  function openEdit(action: GxStandardAction) {
    setForm({ label: action.label, icon: action.icon, severity: action.severity });
    setEditingAction(action);
    setError('');
    setShowModal(true);
  }

  async function handleDelete(action: GxStandardAction) {
    if (!confirm(`Deactivate action "${action.label}"?`)) return;
    try {
      await deleteStandardAction(eventId, action.id);
      fetchActions();
    } catch {
      alert('Failed to deactivate action.');
    }
  }

  async function handleSave() {
    if (!form.label.trim()) {
      setError('Label is required.');
      return;
    }
    if (!form.icon.trim()) {
      setError('Icon is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingAction) {
        const data: UpdateStandardActionData = {
          label: form.label.trim(),
          icon: form.icon.trim(),
          severity: form.severity,
        };
        await updateStandardAction(eventId, editingAction.id, data);
      } else {
        const data: CreateStandardActionData = {
          label: form.label.trim(),
          icon: form.icon.trim(),
          severity: form.severity,
        };
        await createStandardAction(eventId, data);
      }
      setShowModal(false);
      fetchActions();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save action.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>Loading actions...</p>;
  }

  return (
    <div>
      {actions.length === 0 ? (
        <p style={styles.empty}>No quick actions configured for this event.</p>
      ) : (
        <div style={styles.list}>
          {actions.map((action) => {
            const sevColor = SEVERITY_COLORS[action.severity] || SEVERITY_COLORS.Low;
            return (
              <div key={action.id} style={styles.actionRow}>
                <span style={styles.icon}>{action.icon}</span>
                <div style={styles.actionInfo}>
                  <div style={styles.actionLabel}>{action.label}</div>
                  <div style={styles.actionMeta}>
                    <span
                      style={{
                        ...styles.severityBadge,
                        backgroundColor: sevColor.bg,
                        color: sevColor.color,
                      }}
                    >
                      {action.severity}
                    </span>
                  </div>
                </div>
                <span
                  style={{
                    ...styles.statusBadge,
                    ...(action.active ? styles.activeStatus : styles.inactiveStatus),
                  }}
                >
                  {action.active ? 'Active' : 'Inactive'}
                </span>
                <button style={styles.actionBtn} onClick={() => openEdit(action)} title="Edit">✏️</button>
                <button style={styles.actionBtn} onClick={() => handleDelete(action)} title="Deactivate">🗑️</button>
              </div>
            );
          })}
        </div>
      )}

      <button style={styles.addButton} onClick={openAdd}>+ Add Action</button>

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>{editingAction ? 'Edit Action' : 'Add Action'}</h3>
            {error && <div style={styles.error}>{error}</div>}
            <div style={styles.field}>
              <label style={styles.label}>Label</label>
              <input
                style={styles.input}
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Request Cleanup"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Icon (emoji)</label>
              <input
                style={styles.input}
                type="text"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="e.g. 🧹"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Severity</label>
              <select
                style={styles.select}
                value={form.severity}
                onChange={(e) => setForm({ ...form, severity: e.target.value as FeedItemSeverity })}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
